import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import coaService from "../../services/coaService";
import { FaArrowLeft, FaSave } from "react-icons/fa";

const COAForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountLevel, setAccountLevel] = useState("Main Ledger");
  const [formData, setFormData] = useState({
    account_code: "",
    account_name: "",
    account_type: "Asset",
    description: "",
    parent_id: null,
    is_active: true,
    opening_balance: 0,
  });

  useEffect(() => {
    fetchAccounts();
    if (id) {
      fetchAccount();
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const data = await coaService.getAccounts();
      setAccounts(data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    }
  };

  const fetchAccount = async () => {
    try {
      const data = await coaService.getAccount(id);
      setFormData(data);
      if (data.parent_id) {
         setAccountLevel("Sub Ledger"); // Default, will refine after fetching all
      }
    } catch (error) {
      console.error("Error fetching account:", error);
    }
  };

  useEffect(() => {
    if (id && accounts.length > 0 && formData.parent_id) {
      const parentAcc = accounts.find(a => String(a.id) === String(formData.parent_id));
      if (parentAcc && parentAcc.parent_id) {
        setAccountLevel("Account Ledger");
      } else {
        setAccountLevel("Sub Ledger");
      }
    }
  }, [id, accounts, formData.parent_id]);

  const handleParentChange = (e) => {
    const pId = e.target.value;
    let accType = formData.account_type;
    if (pId) {
      const parentAcc = accounts.find(a => String(a.id) === String(pId));
      if (parentAcc) {
        accType = parentAcc.account_type;
      }
    }
    setFormData(prev => ({
      ...prev,
      parent_id: pId,
      account_type: accType
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.parent_id === "") payload.parent_id = null;
      if (payload.parent_id !== null) payload.parent_id = parseInt(payload.parent_id, 10);

      if (id) {
        await coaService.updateAccount(id, payload);
      } else {
        await coaService.createAccount(payload);
      }
      navigate("/dashboard/coa/list");
    } catch (error) {
      console.error("Error saving account:", error);
      alert(error.response?.data?.detail || "An error occurred while saving the account.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "800px", margin: "0 auto" }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          background: "none",
          border: "none",
          color: "#4a5568",
          cursor: "pointer",
          marginBottom: "20px",
          fontWeight: "600",
        }}
      >
        <FaArrowLeft /> Back
      </button>

      <div
        style={{
          background: "white",
          padding: "32px",
          borderRadius: "16px",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.05)",
        }}
      >
        <h2 style={{ fontSize: "24px", fontWeight: "700", color: "#1a202c", marginBottom: "24px" }}>
          {id ? "Edit Account" : "Add New Account"}
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            <div>
              <label style={labelStyle}>Account Code</label>
              <input
                type="text"
                name="account_code"
                value={formData.account_code}
                onChange={handleChange}
                required
                placeholder="e.g. 1000"
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>Account Name</label>
              <input
                type="text"
                name="account_name"
                value={formData.account_name}
                onChange={handleChange}
                required
                placeholder="e.g. Cash at Bank"
                style={inputStyle}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Account Level</label>
            <div style={{ display: "flex", gap: "20px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "15px", color: "#4a5568" }}>
                <input 
                  type="radio" 
                  value="Main Ledger" 
                  checked={accountLevel === "Main Ledger"} 
                  onChange={(e) => { setAccountLevel(e.target.value); setFormData(p => ({...p, parent_id: null})); }} 
                /> 
                Main Ledger
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "15px", color: "#4a5568" }}>
                <input 
                  type="radio" 
                  value="Sub Ledger" 
                  checked={accountLevel === "Sub Ledger"} 
                  onChange={(e) => { setAccountLevel(e.target.value); setFormData(p => ({...p, parent_id: null})); }} 
                /> 
                Sub Ledger
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "15px", color: "#4a5568" }}>
                <input 
                  type="radio" 
                  value="Account Ledger" 
                  checked={accountLevel === "Account Ledger"} 
                  onChange={(e) => { setAccountLevel(e.target.value); setFormData(p => ({...p, parent_id: null})); }} 
                /> 
                Account Ledger
              </label>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "20px" }}>
            {accountLevel === "Main Ledger" && (
              <div>
                <label style={labelStyle}>Account Type</label>
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                >
                  <option value="Asset">Asset</option>
                  <option value="Liability">Liability</option>
                  <option value="Equity">Equity</option>
                  <option value="Revenue">Revenue</option>
                  <option value="Expense">Expense</option>
                </select>
              </div>
            )}
            {accountLevel === "Sub Ledger" && (
              <div>
                <label style={labelStyle}>Select Main Ledger</label>
                <select
                  name="parent_id"
                  value={formData.parent_id || ""}
                  onChange={handleParentChange}
                  required
                  style={inputStyle}
                >
                  <option value="">-- Select Main Ledger --</option>
                  {accounts.filter(a => a.parent_id === null && String(a.id) !== String(id)).map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_code} - {acc.account_name} ({acc.account_type})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {accountLevel === "Account Ledger" && (
              <div>
                <label style={labelStyle}>Select Sub Ledger</label>
                <select
                  name="parent_id"
                  value={formData.parent_id || ""}
                  onChange={handleParentChange}
                  required
                  style={inputStyle}
                >
                  <option value="">-- Select Sub Ledger --</option>
                  {accounts.filter(a => a.parent_id !== null && String(a.id) !== String(id)).map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.account_code} - {acc.account_name} ({acc.account_type})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label style={labelStyle}>Opening Balance</label>
              <input
                type="number"
                name="opening_balance"
                value={formData.opening_balance}
                onChange={handleChange}
                style={inputStyle}
              />
            </div>
          </div>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
              style={{ ...inputStyle, resize: "vertical" }}
              placeholder="Enter account description..."
            />
          </div>

          <div style={{ marginBottom: "30px", display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="checkbox"
              name="is_active"
              id="is_active"
              checked={formData.is_active}
              onChange={handleChange}
              style={{ width: "18px", height: "18px" }}
            />
            <label htmlFor="is_active" style={{ ...labelStyle, marginBottom: 0, cursor: "pointer" }}>
              Is Active
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              background: "linear-gradient(135deg, #00aaff 0%, #0077cc 100%)",
              color: "white",
              padding: "14px",
              borderRadius: "12px",
              border: "none",
              fontWeight: "700",
              fontSize: "16px",
              cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0, 119, 204, 0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "10px",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Saving..." : <><FaSave /> Save Account</>}
          </button>
        </form>
      </div>
    </div>
  );
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "14px",
  fontWeight: "600",
  color: "#4a5568",
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  outline: "none",
  fontSize: "15px",
  transition: "border-color 0.2s",
};

export default COAForm;
